// The docs pages (content/docs). Each page renders as a React Server Component in the `rsc`
// environment: the response carries the rendered article (an RSC payload) and the page's metadata
// and headings, so the browser gets neither the compiled MDX nor its static components.

import docsCovers from "virtual:calmlens/docs-covers";
import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { renderServerComponent } from "@tanstack/react-start/rsc";
import { isValidElement, type ReactNode } from "react";
import Article from "src/components/common/Article/Article";
import { getDocsPageJsonLd } from "src/lib/DocsJsonLd";
import type { DocsHeading } from "src/lib/DocsPages";
import { serializeJsonLd } from "src/lib/LandingJsonLd";
import { withHydrationMarker } from "src/lib/ServerContent";
import { docsSource } from "src/server/docs/DocsSource";
import { renderCachedServerComponent } from "src/server/FlightCache";

/** The text of a heading's rendered content, as the browser's `textContent` reads it. */
const textOf = (node: ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return "";
};

export const getDocsPage = createServerFn({ method: "GET" })
  .validator((path: string) => path)
  .handler(async ({ data: path }) => {
    const slugs = path.split("/").filter(Boolean);
    const page = docsSource.getPage(slugs);
    if (!page) throw notFound();
    const { body, toc, lastModified } = await page.data.load();
    // The tables of contents list the headings below the title; the changelog lists its versions.
    const maxDepth = page.url === "/docs/changelog" ? 2 : 6;
    const headings: DocsHeading[] = toc
      .filter((item) => item.depth >= 2 && item.depth <= maxDepth)
      .map((item) => ({ id: item.url.slice(1), value: textOf(item.title), depth: item.depth }));
    const docsPath = page.slugs.join("/");
    const summary = {
      path: page.url,
      title: page.data.title ?? "",
      seoTitle: page.data.seoTitle,
      description: page.data.description,
      lastUpdated: lastModified?.toISOString(),
      // The artwork's cover, whose social card the page's head links (vite/docsSocialImages.ts).
      cover: Object.hasOwn(docsCovers, docsPath) ? docsCovers[docsPath] : undefined,
    };
    return {
      ...summary,
      headings,
      content: await renderCachedServerComponent(`docs:${page.url}`, () =>
        renderServerComponent(
          withHydrationMarker(
            <>
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: serializeJsonLd(getDocsPageJsonLd(summary)) }}
              />
              <Article Body={body} sectionDividers />
            </>,
          ),
        ),
      ),
    };
  });
